import { isOpenInApp, stringToBoolean } from "@repo/shared-utils/pwa-utils";
import { allowedRoutes } from "../constants/TradingMallConstants";
import { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import { redirect } from "next/navigation";

export const isAllowedUrl = (url: string): boolean => {
  return allowedRoutes.some(route => typeof route === "string" ? route === url : route.test(url));
}

export const getAbsoluteUrl = (url: string): string => {
  if(isOpenInApp() && !url.includes("app-v1")){
    return `/app-v1${url}`
  }
  return url
}

export const handleRedirection = (
  rawUrl: string,
  router?: AppRouterInstance,
  inNewTab: string = "false",
  headerName: string = "Bajaj Broking"
) => {
  const url = rawUrl.trim();
  const isAllowed = isAllowedUrl(url);
  const openInNewTab = stringToBoolean(inNewTab);

  const openUrl = (targetUrl: string) => {
    const absoluteUrl = getAbsoluteUrl(targetUrl);

    if (typeof window !== "undefined") {
      window.open(absoluteUrl, openInNewTab ? "_blank" : "_self");
    } else {
      redirect(absoluteUrl);
    }
  };

  if (isAllowed) {
    const absoluteUrl = getAbsoluteUrl(url);
    if (router) {
      router.push(absoluteUrl);
    } else {
      redirect(absoluteUrl);
    }
    return;
  }

  if (isOpenInApp()) {
      if (!url?.includes("bajajfinserv.in") && url?.startsWith("https:")) {
        window.prompt(
          `openInAppWebViewActivity://{"url":"${url}","title":"${headerName ?? "Bajaj Security"}"}`
        );
      } else if (url?.includes("bajajsuperapp") && !url?.startsWith("https:")) {
        const windowWithTarsier = window as Window & {
          tarsier?: {
            invokePrompt: (prompt: string, force?: boolean) => unknown;
          };
        };
        windowWithTarsier.tarsier?.invokePrompt(`inAppDeepLink://${url}`);
      } else {
        openUrl(url);
      }
  
  } else {
    openUrl(url);
  }
};


export const getFullUrl = (url: string): string => {
  if (!url) return "";
  if (url.startsWith("http")) return url;

  const path = url.startsWith("/") ? url : `/${url}`;

  return process.env.NEXT_PUBLIC_SITE_URL ? `${process.env.NEXT_PUBLIC_SITE_URL}${path}` : path;
};

export const pickFirst = (source: unknown, keys: string[]): unknown => {
  if (!source || typeof source !== "object") {
    return undefined;
  }
  const sourceMap = source as Record<string, unknown>;

  for (const key of keys) {
    const value = sourceMap[key];
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return undefined;
};

/**
 * Converts a Unix timestamp (in seconds) to a relative time string
 * e.g. "Just now", "5m ago", "2h ago", "3d ago", or a formatted date.
 */
export const formatTimestamp = (
  ts: string | number | undefined,
  labels?: {
    justNow?: string;
    minutesAgo?: string;
    hoursAgo?: string;
    daysAgo?: string;
  },
): string => {
  if (!ts) return "";

  const date =
    typeof ts === "string"
      ? new Date(Number.parseInt(ts, 10) * 1000)
      : new Date(Number(ts) * 1000);

  if (Number.isNaN(date.getTime())) return "";

  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  const justNow = labels?.justNow ?? " Just now";
  const minutesAgo = labels?.minutesAgo ?? " Minutes ago";
  const hoursAgo = labels?.hoursAgo ?? " Hours ago";
  const daysAgo = labels?.daysAgo ?? " Days ago";

  if (diffInSeconds < 60) return justNow;
  if (diffInSeconds < 3600)
    return `${Math.floor(diffInSeconds / 60)}${minutesAgo}`;
  if (diffInSeconds < 86400)
    return `${Math.floor(diffInSeconds / 3600)}${hoursAgo}`;
  // Keep relative "X days ago" visible for a longer window (up to ~30 days)
  if (diffInSeconds < 2592000)
    return `${Math.floor(diffInSeconds / 86400)}${daysAgo}`;

  return date.toLocaleDateString();
};
